import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { ValidationItems } from '@kanda-libs/ks-component-ts';
import { EmploymentDetails } from '@kanda-libs/ks-frontend-services';

import { EmploymentClassType } from './PrimaryEmployment/types';

export interface MonthlyIncomeValidationPrimary {
  type: Extract<EmploymentClassType, 'primary'>;
  employmentType: EmploymentDetails['employment_status'];
  index: undefined;
  arrayName: undefined;
}

export interface MonthlyIncomeValidationSecondary {
  type: Extract<EmploymentClassType, 'secondary'>;
  employmentType: EmploymentDetails['employment_status'];
  index: number;
  arrayName: string;
}

export type MonthlyIncomeValidationProps =
  | MonthlyIncomeValidationPrimary
  | MonthlyIncomeValidationSecondary;

export interface MonthlyIncomeValidationHook {
  validation: ValidationItems;
  onBlur: () => void;
}

export default function useMonthlyIncomeValidation({
  type,
  employmentType,
  arrayName,
  index,
}: MonthlyIncomeValidationProps): MonthlyIncomeValidationHook {
  const grossFieldName = useMemo(() => {
    if (type === 'primary')
      return `credit.employment_details.gross_annual_income.amount`;
    return `${arrayName}.${index}.gross_annual_income.amount`;
  }, [type, arrayName, index]);

  const fieldName = useMemo(() => {
    if (type === 'primary')
      return `credit.employment_details.${employmentType}_details.monthly_income_after_tax`;
    return `${arrayName}.${index}.${employmentType}_details.monthly_income_after_tax`;
  }, [type, arrayName, index, employmentType]);

  const grossAnnualIncome = useWatch({
    name: grossFieldName,
  });
  const { trigger } = useFormContext();

  const validateMonthlyIncome = useCallback(
    (monthlyIncome?: string | number | boolean): boolean => {
      if (
        typeof monthlyIncome !== 'number' ||
        typeof grossAnnualIncome !== 'number'
      )
        return false;
      if (!monthlyIncome || !grossAnnualIncome) return false;
      return Boolean(
        monthlyIncome &&
          grossAnnualIncome &&
          monthlyIncome <= grossAnnualIncome,
      );
    },
    [grossAnnualIncome],
  );

  const validation: ValidationItems = useMemo(
    () => ({
      min: { value: 100, message: 'Amount must be greater than £1' },
      required: {
        value: true,
        message: 'Monthly income is required',
      },
      validate: {
        value: (value) => validateMonthlyIncome(value),
        message: 'Monthly income cannot be greater than gross annual income',
      },
    }),
    [validateMonthlyIncome],
  );

  const onBlur = useCallback(() => trigger(fieldName), [trigger, fieldName]);

  return {
    validation,
    onBlur,
  };
}
